import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';

import { CommsService } from '@services/comms/comms.service';
import { UtilsService } from '@services/utils/utils.service';
import { UserService } from '@services/user/user.service';

import { TranslateService } from '@ngx-translate/core';
import { Events } from '@services/events/events.service';
import { Module, Feature, SubscriberService } from '@services/subscriber/subscriber.service';
import { cloneDeep, difference, each, find, get, has, intersection, map, some, union } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class RulesService {

  public filterObject: any = {};

  public rules = {
    lastRequest: 0,
    data: []
  };

  public zones = {
    data: []
  };

  public ruleTypes = [
    {
      id: 'observation',
      description: 'SHARED.Observation',
    },
    {
      id: 'beacon',
      description: this.translate.instant('COMMONT_SERVICE.Missing_Beacon'),
    },
    {
      id: 'asset',
      description: this.translate.instant('SHARED.Asset'),
      features: [ Feature.PROPERTIES]
    },
    // {
    //   id: 'powerpoint',
    //   description: this.translate.instant('SHARED.Points'),
    //   features: [ Feature.LOYALTY]
    // },
    // {
    //   id: 'feedback',
    //   description: this.translate.instant('SHARED.Feedback'),
    //   modules: [ Module.FEEDBACK]
    // },
    {
      id: 'topic',
      description: this.translate.instant('SHARED.Topic'),
      modules: [ Module.TOPICS]
    },
    {
      id: 'response',
      description: this.translate.instant('SHARED.Check'),
      modules: [ Module.CHECKS]
    },
    {
      id: 'content',
      description: this.translate.instant('CONTENT.Module_Name'),
      modules: [ Module.CONTENT]
    },
    {
      id: 'task',
      description: this.translate.instant('SHARED.Task')
    }
    /* {
      id: 'proximity',
      description: this.translate.instant('SHARED.Location_Worker_Proximity'),
      tooltip: this.translate.instant('SHARED.Location_Worker_Proximity')
    }, */
    /* {
      id: "obsdets",
      description: "Observation Detail",
      tooltip: "A report that displays information about individual observations"
    },*/
    /* {
      id: "team",
      description: "Time by Team"
    },
    {
      id: "worker",
      description: "Time Summary",
      tooltip: "A report that shows information about how time was spent"
    }
    */
  ];

  public likelihoods = [
    {
      id: 'medium',
      description: this.translate.instant('SHARED.Low'),
      class: 'severity-medium'
    },
    {
      id: 'high',
      description: this.translate.instant('SHARED.Medium'),
      class: 'severity-high'
    },
    {
      id: 'highest',
      description: this.translate.instant('SHARED.High'),
      class: 'severity-highest'
    }
  ];

  public severities = [
    {
      id: 'medium',
      description: this.translate.instant('SHARED.Low'),
      class: 'severity-medium'
    },
    {
      id: 'high',
      description: this.translate.instant('SHARED.Medium'),
      class: 'severity-high'
    },
    {
      id: 'highest',
      description: this.translate.instant('SHARED.High'),
      class: 'severity-highest'
    }
  ];

  constructor(
    private logger: NGXLogger,
    private comms: CommsService,
    private utils: UtilsService,
    private userService: UserService,
    private utilsService: UtilsService,
    protected translate: TranslateService,
    private events: Events,
    private subscriber: SubscriberService
  ) {
  }

  public availableTypes() {
    const ret = [];
    each(this.ruleTypes, item => {
      let matched = 0;
      if (!item?.features && !item.modules) {
        matched = 1;
      }
      if (has(item, 'features')) {
        each(item.features, feature => {
          if (this.subscriber.usesFeature(feature)) {
            matched = 1;
          }
        });
      }
      if (!matched && has(item, 'modules')) {
        each(item.modules, module => {
          if (this.subscriber.usesModule(module)) {
            matched = 1;
          }
        });
      }
      if (matched) {
        ret.push(item);
      }
    });
    return ret;
  }

  public fetchRules(updated: number = 0) {
    if (updated && updated < this.rules.lastRequest) {
      this.logger.log(`local rules cache already up to date: ${updated}, ${this.rules.lastRequest}`);
      return Promise.resolve(this.rules.data);
    } else {
      return new Promise((resolve, reject) => {
        const when = Date.now();
        this.comms.sendMessage({
          cmd: 'getRules',
          lastRequest: this.rules.lastRequest,
          sendTime: when
        }, false, true)
          .then((data) => {
            if (data && data.reqStatus === 'OK') {
              this.updateCache(data);
            }
            resolve(this.rules.data);
          }).catch((err) => {
          reject(err);
        });
      });
    }
  }

  public updateCache(data) {
    this.rules.lastRequest = data.result.timestamp;
    this.rules.data = data.result.rules;
    this.utils.sortArray(this.rules.data, [
      {
        name: 'name',
        function: (item) => {
          if (item && typeof item === 'string') {
            return item.toLowerCase();
          } else {
            return item;
          }
        }
      }
    ]);
    this.events.publish('ccs:rulesUpdate');
  }

  public getRuleByID(id: number | string): any {
    const r = find(this.rules.data, [ 'ruleID',  id ]);
    if (r) {
      return r;
    } else {
      return null;
    }
  }

  public clearCache() {
    this.rules.lastRequest = null;
    this.rules.data = null;
  }

  ruleTypeName(type: string) {
    const r = find(this.ruleTypes, {id: type});
    if (r) {
      return this.translate.instant(r.description);
    } else {
      return 'unknown';
    }
  }

  public ruleNameByID(ruleID) {
    let ret = null;
    $.each(this.rules.data, (i, comp) => {
      if (comp.ruleID === +ruleID) {
        ret = comp;
        return false;
      }
    });
    return this.ruleName(ret);
  }

  public ruleName(item) {
    const ret = item.name;
    return ret;
  }

  public colorDot(val): string {
    return `assets/images/${val}Dot.svg`;
  }

  /**
   * Observation-rule selectors from the rule form. Shared by add and update so
   * the two cannot drift: update once omitted targetGroups/recipients, which
   * silently dropped those criteria from any edited compliment rule.
   */
  observationSelectors(fData, savedRecipients: number[] = []) {
    const obstype: string[] = this.utilsService.makeArray(fData.obstype);
    const behavior = obstype.includes('behavior');
    const compliment = obstype.includes('compliment');
    // `recipients` is one backend selector shown as two fields: Receiving users
    // (compliment) and Tagged users (behavior). Read the field(s) on show for the
    // selected types, so a value left in a hidden field is not saved.
    const receiving = this.utilsService.makeArray(fData.recipients, parseInt);
    const tagged = this.utilsService.makeArray(fData.taggedUsers, parseInt);
    let recipients = !behavior ? receiving : tagged;
    if (behavior && compliment) {
      // both fields open on the same saved list: a user added in either is kept,
      // and a user removed from either is dropped
      const removed = difference(map(savedRecipients, Number), intersection(receiving, tagged));
      recipients = difference(union(receiving, tagged), removed);
    }
    return {
      obstype,
      states: this.utilsService.makeArray(fData.states),
      hasWorkorder: (fData.hasWorkorder === undefined || fData.hasWorkorder === '') ? 0 : 1,
      locations: this.utilsService.makeArray(fData.locations, parseInt),
      zones: this.utils.makeArray(fData.zones, zone => zone),
      severities: this.utilsService.makeArray(fData.severities),
      likelihoods: this.utilsService.makeArray(fData.likelihoods),
      creators: this.utilsService.makeArray(fData.creators, parseInt),
      owners: this.utilsService.makeArray(fData.owners, parseInt),
      groups: this.utilsService.makeArray(fData.groups, parseInt),
      targetGroups: this.utilsService.makeArray(fData.targetGroups, parseInt),
      recipients,
      // tagged users' teams only apply to behavior rules
      recipientTeams: behavior ? this.utilsService.makeArray(fData.recipientTeams, parseInt) : [],
      // the flipswitch submits '' when off; a reopened rule carries 0
      recipientTeamsAnyTeam: (behavior && ![undefined, null, '', 0, '0'].includes(fData.recipientTeamsAnyTeam)) ? 1 : 0
    };
  }

  handleAddRule(fData) {
    if (fData.type === 'observation') {
      fData.selectors = this.observationSelectors(fData);
    } else if (fData.type === 'beacon') {
      fData.selectors = {
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
      };
    } else if (fData.type === 'proximity') {
      fData.selectors = {
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
        groups: this.utilsService.makeArray(fData.groups, parseInt),
        workers: this.utilsService.makeArray(fData.workers, parseInt)
      };
    } else if (fData.type === 'response') {
      fData.selectors = {
        states: this.utilsService.makeArray(fData.states),
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
        assignees: this.utilsService.makeArray(fData.assignees),
        checks: this.utilsService.makeArray(fData.checks),
        checkTypes: this.utilsService.makeArray(fData.checkTypes),
        checkResults: this.utilsService.makeArray(fData.checkResults),
        deployments: this.utilsService.makeArray(fData.deployments),
      };
    } else if (fData.type === 'task') {
      fData.selectors = {
        states: this.utilsService.makeArray(fData.states)
      };
    } else if (fData.type === 'asset') {
      // Preserve existing selectors (e.g., properties from encodeData)
      const existingSelectors = fData.selectors || {};
      fData.selectors = {
        ...existingSelectors,
        states: this.utilsService.makeArray(fData.states),
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
        assets: fData.assets
      };
    } else if (fData.type === 'topic') {
      fData.selectors = {
        states: this.utilsService.makeArray(fData.states),
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        topics: fData.topics
      };
    } else if (fData.type === 'content') {
    // Preserve existing selectors (e.g., properties from encodeData)
    const existingSelectors = fData.selectors || {};
    fData.selectors = {
      ...existingSelectors,
      states: this.utilsService.makeArray(fData.states),
      objects: fData.objects,
      status: this.utilsService.makeArray(fData.status),
    };
    if (fData.instant === '') {
      const states = fData.selectors.states;
      if (!states.length || has(states, 'review_expiring')) {
        fData.selectors.review_expiring = fData.review_expiring;
        fData.selectors.review_expiring_unit = fData.review_expiring_unit;
      }
      if (!states.length || has(states, 'validity_expiring')) {
        fData.selectors.validity_expiring = fData.validity_expiring;
        fData.selectors.validity_expiring_unit = fData.validity_expiring_unit;
      }
    }
    }

    if (fData.instant === '') {
      fData.interval = this.toSeconds(fData.interval, fData.interval_unit);
    } else {
      fData.interval = 0;
    }
    fData.frequency = this.toSeconds(fData.frequency, fData.frequency_unit);
    fData.recipientSelectors = {
      targets: this.utilsService.makeArray(fData.targets),
      targetUsers: this.utilsService.makeArray(fData.targetUsers, parseInt),
      sendToGroups: this.utilsService.makeArray(fData.sendToGroups, parseInt),
      sendToAnyGroup: (fData.sendToAnyGroup === undefined || fData.sendToAnyGroup === '') ? 0 : 1,
      sendToRoles: this.utilsService.makeArray(fData.sendToRoles, parseInt),
      sendToCertifications: this.utilsService.makeArray(fData.sendToCertifications, parseInt),
      sendToPermissions: this.utilsService.makeArray(fData.sendToPermissions)
    };
    if ( has(fData, 'onlyActive') ) {
      fData.recipientSelectors['onlyActive'] = fData.onlyActive !== '' ? 1 : 0;
    }

    if (fData.active === '') {
      fData.active = 0;
    }

    fData.cmd = 'addRule';
    fData.sendTime = Date.now();
    return this._updateAndRefresh(this.encodeRule(fData), () => this.fetchRules());
  }

  toSeconds(number, unit) {
    if (number === undefined || !number) {
      return 0;
    }
    if (unit === undefined) {
      return number;
    }
    if (unit === 'weeks') {
      return number * 604800;
    } else if (unit === 'days') {
      return number * 86400;
    } else if (unit === 'hours') {
      return number * 3600;
    } else if (unit === 'minutes') {
      return number * 60;
    } else {
      return number;
    }
  }

  encodeRule(theRule) {
    const r = {};
    Object.keys(theRule).forEach((key) => {
      let v;
      let o;
      // selectors is an
      if ((key === 'selectors' || key === 'recipientSelectors') && typeof theRule[key] !== 'string') {
        // preferences used to be in here too - but those are not managed on the dashboard
        // these are JSON objects that need to be a string
        v = theRule[key];
        o = {};
        if (typeof (v) === 'object') {
          if (Array.isArray(v)) {
            $.each(v, (i, item) => {
              o[item] = 1;
            });
          } else {
            o = v;
          }
        }
        r[key] = JSON.stringify(o);
      } else {
        r[key] = theRule[key];
      }
    });
    return r;
  }

  handleUpdateRuleFolder(data) {
    data.cmd = 'updateRule';
    return this._updateAndRefresh(data, () => this.fetchRules());
  }

  handleUpdateRule(fData) {
    if (fData.type === 'observation') {
      fData.selectors = this.observationSelectors(fData, get(this.getRuleByID(+fData.ruleID), 'selectors.recipients', []));
    } else if (fData.type === 'response') {
      fData.selectors = {
        states: this.utilsService.makeArray(fData.states),
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
        assignees: this.utilsService.makeArray(fData.assignees),
        checks: this.utilsService.makeArray(fData.checks),
        deployments: this.utilsService.makeArray(fData.deployments),
        checkResults: this.utilsService.makeArray(fData.checkResults),
        expiringWhen: fData?.expiringWhen ?? 0
      };
    } else if (fData.type === 'task') {
      fData.selectors = {
        states: this.utilsService.makeArray(fData.states)
      };
    } else if (fData.type === 'asset') {
      // Preserve existing selectors (e.g., properties from encodeData)
      const existingSelectors = fData.selectors || {};
      fData.selectors = {
        ...existingSelectors,
        states: this.utilsService.makeArray(fData.states),
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
        assets: fData.assets
      };
    } else if (fData.type === 'beacon') {
      fData.selectors = {
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
      };
    } else if (fData.type === 'proximity') {
      fData.selectors = {
        locations: this.utilsService.makeArray(fData.locations, parseInt),
        zones: this.utils.makeArray(fData.zones, zone => zone),
        groups: this.utilsService.makeArray(fData.groups, parseInt),
        workers: this.utilsService.makeArray(fData.workers, parseInt),
        limit: +fData.limit
      };
  } else if (fData.type === 'content') {
    // Preserve existing selectors (e.g., properties from encodeData)
    const existingSelectors = fData.selectors || {};
    fData.selectors = {
      ...existingSelectors,
      states: this.utilsService.makeArray(fData.states),
      objects: fData.objects,
      status: this.utilsService.makeArray(fData.status),
    };
    if (fData.instant === '') {
      const states = fData.selectors.states;
      if (!states.length || has(states, 'review_expiring')) {
        fData.selectors.review_expiring = fData.review_expiring;
        fData.selectors.review_expiring_unit = fData.review_expiring_unit;
      }
      if (!states.length || has(states, 'validity_expiring')) {
        fData.selectors.validity_expiring = fData.validity_expiring;
        fData.selectors.validity_expiring_unit = fData.validity_expiring_unit;
      }
    }
  }
    if (fData.instant === '') {
      fData.interval = this.toSeconds(fData.interval, fData.interval_unit);
    } else {
      fData.interval = 0;
    }
    fData.frequency = this.toSeconds(fData.frequency, fData.frequency_unit);
    fData.recipientSelectors = {
      targets: this.utilsService.makeArray(fData.targets),
      targetUsers: this.utilsService.makeArray(fData.targetUsers, parseInt),
      sendToGroups: this.utilsService.makeArray(fData.sendToGroups, parseInt),
      sendToAnyGroup: (fData.sendToAnyGroup === undefined || fData.sendToAnyGroup === '') ? 0 : 1,
      sendToRoles: this.utilsService.makeArray(fData.sendToRoles, parseInt),
      sendToCertifications: this.utilsService.makeArray(fData.sendToCertifications, parseInt),
      sendToPermissions: this.utilsService.makeArray(fData.sendToPermissions)
    };
    if ( has(fData, 'onlyActive') ) {
      fData.recipientSelectors['onlyActive'] = fData.onlyActive !== '' ? 1 : 0;
    }
    if (fData.active === '') {
      fData.active = 0;
    }
    fData.cmd = 'updateRule';
    fData.sendTime = Date.now();
    return this._updateAndRefresh(this.encodeRule(fData), () => this.fetchRules());
  }

  handleDeleteRule(fData) {
    fData.cmd = 'deleteRule';
    return this._updateAndRefresh(fData, () => this.fetchRules());
  }

  private _updateAndRefresh(fData, updateHandler): Promise<any> {
    const cmdOpts = cloneDeep(fData);

    return new Promise((resolve, reject) => {
      this.comms.sendMessage(cmdOpts, false, false)
        .then((data) => {
          if (data && data.reqStatus === 'OK') {
            updateHandler()
              .then((ores) => {
                resolve(data);
              })
              .catch((oerr) => {
                this.logger.error('update failed: ' + oerr);
              });
          } else {
            resolve(data);
          }
        })
        .catch((err) => {
          this.logger.error('update failed: ' + err);
          reject(err);
        });
    });
  }

}
